import { Map as ImmutableMap, List as ImmutableList } from "immutable";
import { connect } from "react-redux";
import { createSelector } from "reselect";

import { debounce } from "lodash";

import { fillTimelineGapUpward, loadPending, scrollTopTimeline } from "flavours/glitch/actions/timelines";
import StatusList from "flavours/glitch/components/status_list";
import { me } from "flavours/glitch/initial_state";
import { botGroupOf, parseLanguages } from "flavours/glitch/utils/hidden_languages";

const getRegex = createSelector([
  (state, { regex }) => regex,
], (rawRegex) => {
  let regex = null;

  try {
    regex = rawRegex && new RegExp(rawRegex.trim(), "i");
  } catch (e) {
    // Bad regex, don't affect filters
  }
  return regex;
});

const MIN_DUPLICATE_KEY_LENGTH = 15;

// Timelines of strangers' posts, where hidden languages apply ("Content and filters").
const LANGUAGE_FILTERED = /^(public|community|hashtag|explore)(:|$)/;

// Cross-posted copies differ in markup, link and hashtag formatting and often
// in alt texts, so the key only uses the plain words of the post text.
const duplicateKey = (status, statuses) => {
  const source = status.get("reblog") ? statuses.get(status.get("reblog")) : status;
  const html = source ? `${source.get("spoiler_text") || ""} ${source.get("content") || ""}` : "";

  const key = html
    .replace(/<a\b[^>]*class="[^"]*\b(?:hashtag|mention)\b[^"]*"[^>]*>.*?<\/a>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&[#a-z0-9]+;/gi, " ")
    .replace(/https?:\/\/\S+/gi, " ")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");

  return key.length >= MIN_DUPLICATE_KEY_LENGTH ? key : null;
};

const makeGetStatusIds = (pending = false) => createSelector([
  (state, { type }) => state.getIn(["settings", type], ImmutableMap()),
  (state, { type }) => state.getIn(["timelines", type, pending ? "pendingItems" : "items"], ImmutableList()),
  (state)           => state.get("statuses"),
  getRegex,
  (state, { hideDuplicates }) => !!hideDuplicates,
  (state, { type }) => (LANGUAGE_FILTERED.test(type) ? state.getIn(["local_settings", "hidden_languages"]) : ""),
  // Accounts only when bots are hidden in this timeline, so other timelines do not
  // recompute on every account update.
  (state, { type }) => (parseLanguages(state.getIn(["local_settings", "hide_bots"])).has(botGroupOf(type)) ? state.get("accounts") : null),
], (columnSettings, statusIds, statuses, regex, hideDuplicates, hiddenLanguagesSetting, botAccounts) => {
  const seen = new Set();
  const hiddenLanguages = parseLanguages(hiddenLanguagesSetting);
  const isBot = accountId => !!botAccounts?.getIn([accountId, "bot"]);

  return statusIds.filter(id => {
    if (id === null) {
      return true;
    }

    const statusForId = statuses.get(id);
    let showStatus    = true;

    // Own posts skip the filters below, but the home column can hide them.
    if (statusForId.get("account") === me) {
      const key = statusForId.get("reblog") === null ? "own" : "own_reblog";
      return columnSettings.getIn(["shows", key]) !== false;
    }

    if (columnSettings.getIn(["shows", "reblog"]) === false) {
      showStatus = showStatus && statusForId.get("reblog") === null;
    }

    if (columnSettings.getIn(["shows", "reply"]) === false) {
      showStatus = showStatus && (statusForId.get("in_reply_to_id") === null || statusForId.get("in_reply_to_account_id") === me);
    }

    // Posts of bots, boosts by bots and boosted posts of bots.
    if (botAccounts && (isBot(statusForId.get("account")) || (statusForId.get("reblog") && isBot(statuses.getIn([statusForId.get("reblog"), "account"]))))) {
      return false;
    }

    if (hiddenLanguages.size > 0) {
      const language = statusForId.get("reblog") ? statuses.getIn([statusForId.get("reblog"), "language"]) : statusForId.get("language");
      if (language && (hiddenLanguages.has(language) || hiddenLanguages.has(language.split(/[-_]/)[0]))) {
        return false;
      }
    }

    if (columnSettings.getIn(["shows", "direct"]) === false) {
      showStatus = showStatus && statusForId.get("visibility") !== "direct";
    }

    if (showStatus && regex) {
      const searchIndex = statusForId.get("reblog") ? statuses.getIn([statusForId.get("reblog"), "search_index"]) : statusForId.get("search_index");
      showStatus = !regex.test(searchIndex);
    }

    if (showStatus && hideDuplicates) {
      const key = duplicateKey(statusForId, statuses);

      if (key !== null) {
        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
      }
    }

    return showStatus;
  });
});

const makeMapStateToProps = () => {
  const getStatusIds = makeGetStatusIds();
  const getPendingStatusIds = makeGetStatusIds(true);

  const mapStateToProps = (state, { timelineId, regex, hideDuplicates }) => ({
    statusIds: getStatusIds(state, { type: timelineId, regex, hideDuplicates }),
    lastId:    state.getIn(["timelines", timelineId, "items"])?.last(),
    isLoading: state.getIn(["timelines", timelineId, "isLoading"], true),
    isPartial: state.getIn(["timelines", timelineId, "isPartial"], false),
    hasMore:   state.getIn(["timelines", timelineId, "hasMore"]),
    numPending: getPendingStatusIds(state, { type: timelineId, hideDuplicates }).size,
  });

  return mapStateToProps;
};

const mapDispatchToProps = (dispatch, { timelineId }) => ({

  onScrollToTop: debounce(() => {
    dispatch(scrollTopTimeline(timelineId, true));
  }, 100),

  onScroll: debounce(() => {
    dispatch(scrollTopTimeline(timelineId, false));
  }, 100),

  onLoadPending: () => dispatch(loadPending(timelineId)),

  onLoadGapUpward: belowId => dispatch(fillTimelineGapUpward(timelineId, belowId)),

});

export default connect(makeMapStateToProps, mapDispatchToProps)(StatusList);
